import { ServiceLogger } from "@/application/services/logger";
import { useRegisterSW } from "virtual:pwa-register/react";

// How often to poll for a new service worker (in ms). This ensures users
// pick up fresh deployments even in long-running sessions.
const SW_UPDATE_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Registers the service worker and sets up an automatic periodic update check.
 *
 * - No offline caching is configured — the SW exists only to satisfy PWA
 *   installability criteria and to detect new deployments.
 * - When a new version is deployed the SW activates via `autoUpdate`, and
 *   `needRefresh` becomes true so the UI can surface a "new version" notice.
 */
export function usePwaUpdates() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl, registration) {
      if (!registration) return;

      // Periodically check for SW updates so long-lived sessions don't miss
      // new deployments.
      setInterval(() => {
        registration.update().catch(() => {
          // Silently ignore — network may be temporarily unavailable.
        });
      }, SW_UPDATE_INTERVAL_MS);

      ServiceLogger.info(`[PWA] Service worker registered: ${swUrl}`);
    },
    onRegisterError(error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      ServiceLogger.warn(
        `[PWA] Service worker registration failed: ${errorMessage}`
      );
    },
  });

  function dismissUpdate() {
    setNeedRefresh(false);
    setOfflineReady(false);
  }

  return {
    /** True when a new version of the app has been deployed. */
    needRefresh,
    /** True when the SW has activated for the first time (first install). */
    offlineReady,
    /** Reload the page to activate the new version. */
    applyUpdate: () => updateServiceWorker(true),
    /** Dismiss the update notification without reloading. */
    dismissUpdate,
  };
}
